// Content for the onboarding flow: question copy, option lists and step
// structure, separated from the MUI rendering in Onboarding.tsx. The real app
// would ship this copy (or serve it from the form builder); the fixtures at
// the bottom stand in for API data.

export type OnboardingMode = 'carer' | 'self';

// Answer keys stay stable while the visible step numbers run 1–TOTAL_STEPS.
export type StepKey = 'fork' | '1' | '2' | '4' | '4a' | '6' | '7' | '8';

// The person being cared for, by name.
//
// It was 'Mum', which is the wireframe's [name] placeholder and reads as a
// relationship rather than as somebody the app holds a record for. The rest of
// the prototype calls her Margaret, and a circle that is hers should say so.
export const RECIPIENT_NAME = 'Margaret';

export const STEP_ORDER: StepKey[] = [
  'fork',
  '1',
  '2',
  '4',
  '4a',
  '6',
  '7',
  '8',
];

export const TOTAL_STEPS = 7;

export const STEP_META: Record<string, { n: number; label: string }> = {
  '1': { n: 1, label: '1' },
  '2': { n: 2, label: '2' },
  '4': { n: 3, label: '3' },
  '4a': { n: 4, label: '4' },
  '6': { n: 5, label: '5' },
  '7': { n: 6, label: '6' },
  '8': { n: 7, label: '7' },
};

// Triage fork shown before step 1. Carers and self-carers go through the
// same steps (phrased differently); explorers skip the gate.
export const FORK_QUESTION =
  'Are you supporting someone, or starting to think about it?';

export const FORK_OPTIONS: {
  label: string;
  mode: OnboardingMode | null;
}[] = [
  { label: "Yes, I'm helping look after someone", mode: 'carer' },
  { label: 'Not yet, just exploring', mode: null },
  { label: 'I need care for myself', mode: 'self' },
];

// Question copy per fork path — same steps, different phrasing.
export const QUESTIONS: Record<OnboardingMode, Record<string, string>> = {
  self: {
    '1': 'Tell us a bit about yourself',
    '2': 'Do you have any diagnosed conditions?',
    '4': 'Is there a care arrangement currently in place?',
    '4a': 'Where are you based?',
    '6': 'How many hours of help do you get each week?',
    '7': 'What do you need help with?',
    '8': 'Finish setting up your care circle',
  },
  carer: {
    '1': 'Who are you caring for?',
    '2': `Does ${RECIPIENT_NAME} have any diagnosed conditions?`,
    '4': 'Is there a care arrangement currently in place?',
    '4a': `Where is ${RECIPIENT_NAME} based?`,
    '6': 'How many hours a week do you spend caring?',
    '7': 'What do you need help with?',
    '8': 'Finish setting up your care circle',
  },
};

export const ARRANGEMENT_OPTIONS = [
  'No',
  'Yes, at home',
  'Yes, care home',
  'Yes, informal',
];

export const POSTCODE_HELPER = 'We use this to find care and support nearby.';

export const HOURS_OPTIONS = [
  'Under 5 hours',
  '5 to 20 hours',
  '20 to 50 hours',
  'Around the clock',
];

export const HELP_OPTIONS = [
  'Finding the right care',
  'Funding and benefits',
  'Legal stuff (PoA, forms)',
  'Understanding what care is needed',
  'Something else',
];

export const CONDITIONS_QUICK_ADD = {
  items: ['Dementia', 'Diabetes', "Parkinson's"],
  addTitle: 'Add a condition',
  placeholder: 'e.g. Arthritis',
};

// Care circle step copy, per fork path.
export const CIRCLE_DESCRIPTION: Record<OnboardingMode, string> = {
  self: 'Invite the people you want using KareHero with you. They get access to the app.',
  carer: `Invite the people helping with ${RECIPIENT_NAME}'s care to use KareHero with you. They get access to the app.`,
};

// Access is all-or-nothing: an invited person either sees the whole care
// circle or none of it. The only extra is whether they can keep their own
// to-dos — on for the people close to the care (family, friends, carers),
// off for professionals like a financial advisor.
//
// Read rather than set. It was a list of switches, which offered a choice that
// is not ours to offer: what somebody can see follows from their relationship,
// so the sheet says what it is instead of asking.
export const PERMISSIONS = [
  {
    key: 'view',
    label: 'See everything',
    desc: 'Finances, documents, care needs and more',
  },
  {
    key: 'actions',
    label: 'Create and edit their own actions',
    desc: 'Add and manage their own to-dos',
  },
];

// What each relationship gets. Everyone can see everything. The "actions"
// capability only exists for the people close to the care (family, friends,
// carers) — professionals like a financial advisor don't get it at all, so
// the key is simply absent and the line never shows.
export const ROLE_PERMS: Record<string, Record<string, boolean>> = {
  'Family member': { view: true, actions: true },
  Friend: { view: true, actions: true },
  Carer: { view: true, actions: true },
  'Financial advisor': { view: true },
  Solicitor: { view: true },
  Other: { view: true },
};

// ─── fixtures (stand-ins for API data) ───────────────────────────────────────

export type CircleMember = {
  initials: string;
  name: string;
  role: string;
  perms: Record<string, boolean>;
};

// One member to start with, and a family one.
//
// It was a financial advisor, which is the least likely person to already be in
// a circle and the only role in the list that cannot keep its own actions: the
// prototype opened on the exception. Financial advisor is still one of the
// relationships you can invite, which is where it belongs.
export const MEMBERS: CircleMember[] = [
  {
    initials: 'SD',
    name: 'Sam Doyle',
    role: 'Family member',
    perms: { view: true, actions: true },
  },
];

/** What the app bar says while these questions are being asked. */
export const ONBOARDING_BAR = 'Tell us about you';
